import React, { useRef, useEffect, useState, useMemo } from "react";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import useAPI from "@toothfairy/shared-api/useApi";
import axios from "axios";
import { generateRowId } from "../../../Utility";
import {
  buildClipboardFile,
  captureTokenFromNameOrUrl,
  clipboardCaptureLabel,
  detectClipboardSource,
  ENABLE_CHIP_COMPOSER,
  extractClipboardParts,
  insertTokenAtCursor,
  normalizeCaptureFileName,
  shouldCaptureAsFile,
  tokenizeForDisplay,
} from "../../../Utility/clipboardUtils";
import AudioRecorder from "./AudioRecorder";
import TokenComposerInput from "./TokenComposerInput";
import envConfig from "../../../../envConfig";
import trainingAPI from "../../../Api/Training/trainingAPI";
import Media from "../../../Api/Media";
import Files from "../../../Api/Files";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";

function hexToRGB(hex, alpha) {
  let expandedHex = hex;
  if (hex.length === 4) {
    expandedHex = "#" + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }
  const r = parseInt(expandedHex.slice(1, 3), 16);
  const g = parseInt(expandedHex.slice(3, 5), 16);
  const b = parseInt(expandedHex.slice(5, 7), 16);
  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } else {
    return `rgb(${r}, ${g}, ${b})`;
  }
}

const convertImageToPng = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) {
          const pngFile = new File(
            [blob],
            file.name.replace(/\.[^.]+$/, ".png"),
            {
              type: "image/png",
            },
          );
          resolve(pngFile);
        } else {
          reject(new Error("Failed to convert image"));
        }
      }, "image/png");
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
};

const ChatMediaLeaf = ({
  onUploaded,
  url,
  type = "image",
  fileExtension = null,
  fileName = null,
  agentColor = null,
}) => {
  const { currentTheme } = AppTheme.useTheme();

  const getTypeIcon = () => {
    if (type === "image") return AppAssets?.icons?.image;
    if (type === "video") return AppAssets?.icons?.video;
    if (type === "audio") return AppAssets?.icons?.audio;
    return AppAssets?.icons?.file;
  };

  const getDisplayText = () => {
    if (fileName) return fileName;
    if (fileExtension) return `.${fileExtension}`;
    return type;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        margin: "0 4px",
        padding: "4px 8px",
        backgroundColor: agentColor
          ? hexToRGB(agentColor, 0.1)
          : currentTheme?.theme?.inputBackground || currentTheme?.theme?.white,
        borderRadius: 6,
        border: `1px solid ${
          agentColor
            ? hexToRGB(agentColor, 0.3)
            : currentTheme?.theme?.border || currentTheme?.theme?.border_color
        }`,
        maxWidth: 400,
        overflow: "hidden",
      }}
    >
      <AppIcon
        icon={getTypeIcon()}
        size={14}
        color={agentColor || currentTheme?.theme?.primary}
        wrapperStyle={{ marginRight: 6 }}
      />
      <span
        style={{
          fontSize: 11,
          color: agentColor || currentTheme?.theme?.font_color,
          maxWidth: 280,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {getDisplayText()}
      </span>
      <AppIcon
        icon={AppAssets?.icons?.close}
        size={12}
        color={agentColor || currentTheme?.theme?.darkGrey}
        wrapperStyle={{ marginLeft: 6 }}
        isClickable
        onPress={() => onUploaded(null)}
      />
    </div>
  );
};

const controller = new AbortController();
const source = axios.CancelToken.source();

const ChatWebInput = ({
  placeholder = "Ask me anything",
  onSend = () => {},
  isStreaming,
  onCancel = () => {},
  agentColor,
  allowStop = false,
  isLastMessageEmpty,
  quickQuestions = [],
  isChatEmpty = false,
  allowImagesUpload = false,
  allowAudiosUpload = false,
  allowVideosUpload = false,
  allowDocsUpload = false,
  onImageUploaded = () => {},
  onAudioUploaded = () => {},
  onVideoUploaded = () => {},
  onFileUploaded = () => {},
  onFilesUploaded = () => {},
  videoUrl,
  setVideoUrl,
  imageUrl,
  setImageUrl,
  audioUrl,
  setAudioUrl,
  fileUrl,
  setFileUrl,
  fileUrls,
  setFileUrls,
  imageUrls,
  audioUrls,
  autoVoiceMode = false,
  voiceSensitivity = "medium",
  isAutoReplyMode = false,
  encryptedToken_,
}) => {
  const AppToasty = AppToast.useToast();
  const { currentTheme, mode } = AppTheme.useTheme();
  const textareaRef = useRef();
  // Counts clipboard-pasted images so each gets a unique name (image 1, image 2, ...)
  const pastedImageCounterRef = useRef(0);
  // Counts clipboard captures of rich/large text (<clipboard 1>, <clipboard 2>, ...)
  const pastedClipboardCounterRef = useRef(0);
  // Friendly labels for clipboard capture pills, e.g. "<clipboard 1>" ->
  // "Excel capture 1" (set at paste time from the detected source app)
  const [clipboardLabels, setClipboardLabels] = useState({});
  // Composer text mirror (used to hide attachment chips whose capture token
  // is inlined in the input; deleting the pill makes the chip reappear)
  const [composerText, setComposerText] = useState("");
  const captureTokensInText = React.useMemo(
    () =>
      new Set(
        tokenizeForDisplay(composerText)
          .filter((segment) => segment.kind !== "text")
          .map((segment) => segment.value),
      ),
    [composerText],
  );
  const isCaptureChipVisible = (nameOrUrl) => {
    const token = captureTokenFromNameOrUrl(nameOrUrl);
    return !token || !captureTokensInText.has(token);
  };
  // Deleting a capture pill (backspace or inline ✕) removes the attachment
  // itself - reconciliation keeps files and tokens 1:1. Only files we
  // captured from the clipboard are swept (never regular user uploads). The
  // send guard prevents stripping attachments while a message is being sent
  // (send clears the text first).
  const capturedFileNamesRef = useRef(new Set());
  const sendingRef = useRef(false);
  useEffect(() => {
    if (sendingRef.current) return;
    const sweep = (urls, kind) => {
      // Collect first, remove in descending index order: each removal
      // splices the parent's array, so ascending indices would go stale
      const removals = [];
      (urls || []).forEach((url, index) => {
        const base = normalizeCaptureFileName(
          decodeURIComponent((url || "").split("?")[0].split("/").pop() || ""),
        );
        if (!capturedFileNamesRef.current.has(base)) return;
        const token = captureTokenFromNameOrUrl(url);
        if (token && !captureTokensInText.has(token)) {
          capturedFileNamesRef.current.delete(base);
          removals.push(index);
        }
      });
      removals
        .sort((a, b) => b - a)
        .forEach((index) => onFilesUploaded(null, null, index, kind));
    };
    sweep(fileUrls, "file");
    sweep(imageUrls, "image");
  }, [captureTokensInText, fileUrls, imageUrls]);
  const markSending = () => {
    sendingRef.current = true;
    setTimeout(() => {
      sendingRef.current = false;
    }, 0);
  };
  const [text, setText] = React.useState("");
  const [inputReady, setInputReady] = React.useState(false);
  const [isTyping, setIsTyping] = React.useState(false);
  const [prevScrollHeight, setPrevScrollHeight] = React.useState(0);
  const [isScrollingUp, setIsScrollingUp] = React.useState(true);
  const [prevScrollTop, setPrevScrollTop] = React.useState(0);
  const [fileUploadInProgress, setFileUploadInProgress] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [filesUploadProgress, setFilesUploadProgress] = useState([]);
  const [fileUploadStatus, setFileUploadStatus] = useState("");
  const [isInputEmpty, setIsInputEmpty] = useState(true);
  const [recordingUploadInProgress, setRecordingUploadInProgress] = useState(
    false,
  );
  const [recordingInProgress, setRecordingInProgress] = useState(false);
  const [recordingUploadProgress, setRecordingUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [fileExtension, setFileExtension] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const { state } = AppStateManager.useAppStateManager();

  const {
    data: S3uploadUrlData,
    loading: urlUploadGenerationInProgress,
    apiRequest: S3uploadUrlRequest,
    response: S3uploadResponse,
  } = useAPI(trainingAPI.bulkImportUrlGeneration, envConfig);

  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);

  const {
    loading: audioProcessingInProgress,
    apiRequest: audioProcessingRequest,
    wasInvoked: audioProcessingInvoked,
    data: audioProcessingResult,
  } = useAPI(Media.processAudio, null, () => {});

  const acceptedFileTypes = useMemo(() => {
    const types = [];
    if (allowImagesUpload) {
      types.push(
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/gif",
        "image/bmp",
        "image/webp",
      );
    }
    if (allowAudiosUpload) {
      types.push("audio/wav", "audio/mp4");
    }
    if (allowVideosUpload) {
      types.push("video/mp4");
    }
    if (allowDocsUpload) {
      types.push(
        ".pptx",
        ".ppt",
        ".json",
        ".pdf",
        ".csv",
        ".doc",
        ".docx",
        ".xls",
        ".xlsx",
        ".html",
        ".txt",
        ".md",
        ".py",
        ".js",
        ".java",
        ".cpp",
        ".c",
        ".cs",
        ".php",
        ".rb",
        ".swift",
        ".go",
        ".ts",
        ".rs",
        ".kt",
        ".scala",
        ".m",
        ".r",
        ".pl",
        ".sh",
        ".sql",
        ".vb",
        ".lua",
        ".dart",
        ".groovy",
        ".jl",
        ".hs",
        ".f90",
        ".pas",
        ".elm",
        ".clj",
        ".png",
        ".jpg",
        ".jpeg",
        ".gif",
        ".bmp",
        ".webp",
      );
    }
    return types.join(", ");
  }, [
    allowImagesUpload,
    allowAudiosUpload,
    allowVideosUpload,
    allowDocsUpload,
  ]);

  const showUploadOption = useMemo(() => {
    return (
      allowImagesUpload ||
      allowAudiosUpload ||
      allowVideosUpload ||
      allowDocsUpload
    );
  }, [
    allowImagesUpload,
    allowAudiosUpload,
    allowVideosUpload,
    allowDocsUpload,
  ]);

  useEffect(() => {
    let currentText = "";
    const words = placeholder?.split(" ");
    if (words?.length > 0)
      setTimeout(() => {
        const intervalId = setInterval(() => {
          if (words.length > 0) {
            currentText += words.shift() + " ";
            setText(currentText);
          } else {
            clearInterval(intervalId);
            setInputReady(true);
            setTimeout(() => {
              textareaRef.current?.focus();
            }, 100);
          }
        }, 30);
      }, 20);
  }, [placeholder]);

  React.useEffect(() => {
    if (!isStreaming) textareaRef.current?.focus();
  }, [isStreaming]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      const handleScroll = () => {
        const currentScrollHeight = textarea.scrollHeight;
        const scrollTop = textarea.scrollTop;

        if (textarea.scrollTop < prevScrollTop) {
          setIsScrollingUp(true);
        } else {
          setIsScrollingUp(false);
        }

        setPrevScrollTop(textarea.scrollTop);
        if (prevScrollHeight > currentScrollHeight || isScrollingUp) {
          textarea.removeEventListener("scroll", handleScroll);
          setPrevScrollHeight(currentScrollHeight);
          return;
        }

        if (isTyping) {
          textarea.scrollTop = currentScrollHeight;
          setPrevScrollHeight(currentScrollHeight);
        }
      };

      textarea.addEventListener("scroll", handleScroll);
      textarea.addEventListener("input", () => setIsTyping(true));
      textarea.addEventListener("compositionstart", () => setIsTyping(true));
      textarea.addEventListener("compositionend", () => setIsTyping(false));

      textarea.scrollTop = textarea.scrollHeight;
      setPrevScrollHeight(textarea.scrollHeight);

      return () => {
        textarea.removeEventListener("scroll", handleScroll);
        textarea.removeEventListener("input", () => setIsTyping(true));
        textarea.removeEventListener("compositionstart", () =>
          setIsTyping(true),
        );
        textarea.removeEventListener("compositionend", () =>
          setIsTyping(false),
        );
      };
    }
  }, [isTyping]);

  const onRecordingUploaded = async (url) => {
    const result = await audioProcessingRequest({
      token: encryptedToken_,
      data: {
        audio_file: url,
        workspaceid: state?.workspaceId,
        chat_mode: true,
      },
    });
    if (result?.contents != undefined) {
      textareaRef.current.value += `${result?.contents}`;
    }
  };

  const handleRecordingStarted = () => {
    setRecordingInProgress(true);
  };

  const detectExtensionFromFileName = (fileUurl) => {
    if (!fileUurl) return "";
    try {
      const url = new URL(fileUurl);
      const pathname = url.pathname;
      const lastSlashIndex = pathname.lastIndexOf("/");
      if (lastSlashIndex !== -1) {
        const filename = pathname.substring(lastSlashIndex + 1);
        const dotIndex = filename.lastIndexOf(".");
        if (dotIndex !== -1) {
          return filename.substring(dotIndex + 1);
        }
      }
    } catch (e) {
      const lastSlashIndex = fileUurl.lastIndexOf("/");
      const filename =
        lastSlashIndex !== -1
          ? fileUurl.substring(lastSlashIndex + 1)
          : fileUurl;
      const dotIndex = filename.lastIndexOf(".");
      if (dotIndex !== -1) {
        return filename.substring(dotIndex + 1);
      }
    }
    return "";
  };

  const detectFileName = (fileUurl) => {
    if (!fileUurl) return "file";
    try {
      const url = new URL(fileUurl);
      const pathname = url.pathname;
      const lastSlashIndex = pathname.lastIndexOf("/");
      if (lastSlashIndex !== -1) {
        let filename = pathname.substring(lastSlashIndex + 1);
        const dotIndex = filename.lastIndexOf(".");
        if (dotIndex !== -1) {
          filename = filename.substring(0, dotIndex);
        }
        if (/^\d{13}/.test(filename)) {
          filename = filename.substring(13);
        }
        return filename || "file";
      }
    } catch (e) {
      const lastSlashIndex = fileUurl.lastIndexOf("/");
      let filename =
        lastSlashIndex !== -1
          ? fileUurl.substring(lastSlashIndex + 1)
          : fileUurl;
      const dotIndex = filename.lastIndexOf(".");
      if (dotIndex !== -1) {
        filename = filename.substring(0, dotIndex);
      }
      if (/^\d{13}/.test(filename)) {
        filename = filename.substring(13);
      }
      return filename || "file";
    }
    return "file";
  };

  const handleRecordingCompleted = async (blob) => {
    setRecordingInProgress(false);
    const _file = new File([blob], "audio.wav", {
      type: "audio/wav",
    });
    const audioId = generateRowId();
    const result = await S3uploadUrlRequest({
      filename: `${state?.workspaceId}/${audioId}.wav`,
      importType: "imported_audio_files",
      contentType: "wav",
    });
    const upload = await axios.request({
      method: "put",
      url: JSON.parse(result?.body)?.uploadURL,
      data: _file,
      headers: {
        "Content-Type": _file.type,
      },
      signal: controller.signal,
      onUploadProgress: (p) => {
        setRecordingUploadInProgress(true);
        setRecordingUploadProgress(Math.round((p.loaded * 100) / p.total));
        if (p.loaded === p.total) {
          setRecordingUploadInProgress(false);
          setRecordingUploadProgress(100);
        }
      },
    });
    if (JSON.parse(result?.body).uploadURL) {
      onRecordingUploaded(
        `imported_audio_files/${state?.workspaceId}/${audioId}.wav`,
      );
    }
  };

  const isValidFileSize = (file, mb = 5) => {
    const maxSizeInBytes = mb * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  };

  const getUrl = async (path, type = "image") => {
    let initialPath = "";
    setFileExtension(path?.split(".")?.pop());
    if (type == "image") {
      initialPath = "imported-image";
    } else if (type == "audio") {
      initialPath = "imported_audio_files";
    } else if (type == "video") {
      initialPath = "imported_video_files";
    } else if (type == "file") {
      initialPath = "imported_doc_files";
    } else {
      return "";
    }
    const _url = await S3downloadUrlRequest({
      filename: `${initialPath}/${state?.workspaceId}/${path}`,
      context: "pdf",
      workspaceid: state?.workspaceId,
    });
    if (_url?.url && type == "image") setImageUrl(_url?.url);
    if (_url?.url && type == "video") setVideoUrl(_url?.url);
    if (_url?.url && type == "audio") setAudioUrl(_url?.url);
    if (_url?.url && type == "file") return _url?.url;
  };

  const fileSizeConstant = 20;

  const handleBatchFilesSelection = async (files, type = "file") => {
    try {
      if (!files) return;

      const filesArray = Array.from(files);
      const uploadedFilesUrls = [];
      const presignedFilesUrls = [];

      if (
        filesArray?.length > 5 ||
        filesArray?.length + (fileUrls?.length || 0) > 5
      ) {
        AppToasty.show("Maximum 5 files allowed", {
          type: "danger",
        });
        return;
      }

      const maxFileSize = fileSizeConstant * 1024 * 1024;
      for (let i = 0; i < filesArray.length; i++) {
        if (filesArray[i].size > maxFileSize) {
          AppToasty.show(
            `File ${filesArray[i].name} exceeds ${fileSizeConstant}MB limit`,
            {
              type: "danger",
            },
          );
          return;
        }
      }

      if (!fileUploadInProgress) {
        setFileUploadInProgress(true);
        setFileUploadProgress(0);
      }
      setFileUploadStatus("uploading");

      const totalFiles = filesArray.length;

      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        let filename = file.name;
        filename = filename.replace(/[^a-zA-Z0-9.]/g, "");
        const _fileN = String(Date.now()) + filename;

        if (filename?.length > 100) {
          setFileUploadInProgress(false);
          setFileUploadProgress(0);
          return AppToasty.show("File name too long (max 100 characters)", {
            type: "danger",
          });
        }
        const contentType = filename.slice(
          (Math.max(0, filename.lastIndexOf(".")) || Infinity) + 1,
        );
        let importType = "imported-image";
        if (type === "video") {
          importType = "imported_video_files";
        } else if (type === "audio") {
          importType = "imported_audio_files";
        } else if (type === "file") {
          importType = "imported_doc_files";
        }
        const result = await S3uploadUrlRequest({
          filename: `${state?.workspaceId}/${_fileN}`,
          importType,
          contentType,
        });

        await axios.request({
          method: "put",
          url: JSON.parse(result?.body)?.uploadURL,
          data: file,
          headers: { "Content-Type": file.type },
          cancelToken: source.token,
          onUploadProgress: (p) => {
            const currentFileProgress = Math.round((p.loaded * 100) / p.total);
            const completedFilesProgress = (i * 100) / totalFiles;
            const currentFileContribution = currentFileProgress / totalFiles;
            const overallProgress = Math.round(
              completedFilesProgress + currentFileContribution,
            );
            setFileUploadProgress(overallProgress);
          },
        });
        if (JSON.parse(result?.body).uploadURL) {
          const _url = await getUrl(_fileN, type);
          uploadedFilesUrls.push(
            `imported_doc_files/${state?.workspaceId}/${_fileN}`,
          );
          presignedFilesUrls.push(_url);
        }
      }
      onFilesUploaded(uploadedFilesUrls, presignedFilesUrls);
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setFileUploadInProgress(false);
      setFileUploadProgress(0);
      setFileUploadStatus("");
    }
  };

  const handleFileSelection = async (file, type = "image") => {
    if (!file) return;

    let filename = file.name;
    filename = filename.replace(/[^a-zA-Z0-9.]/g, "");
    const _fileN = String(Date.now()) + filename;

    if (filename?.length > 100) {
      return AppToasty.show("File name too long (max 100 characters)", {
        type: "danger",
      });
    }

    setFileUploadInProgress(true);
    setFileUploadProgress(0);

    try {
      const contentType = filename.slice(
        (Math.max(0, filename.lastIndexOf(".")) || Infinity) + 1,
      );
      let importType = "imported-image";
      if (type === "video") {
        importType = "imported_video_files";
      } else if (type === "audio") {
        importType = "imported_audio_files";
      } else if (type === "file") {
        importType = "imported_doc_files";
      }

      const result = await S3uploadUrlRequest({
        filename: `${state?.workspaceId}/${_fileN}`,
        importType,
        contentType,
      });

      await axios.request({
        method: "put",
        url: JSON.parse(result?.body)?.uploadURL,
        data: file,
        headers: {
          "Content-Type": file.type,
        },
        cancelToken: source.token,
        onUploadProgress: (p) => {
          setFileUploadProgress(Math.round((p.loaded * 100) / p.total));
        },
      });

      if (JSON.parse(result?.body).uploadURL) {
        const _url = getUrl(_fileN, type);

        if (type === "image") {
          onImageUploaded(`imported-image/${state?.workspaceId}/${_fileN}`);
          setImageUrl(_url);
        }
        if (type === "audio") {
          onAudioUploaded(
            `imported_audio_files/${state?.workspaceId}/${_fileN}`,
          );
        }
        if (type === "video") {
          onVideoUploaded(
            `imported_video_files/${state?.workspaceId}/${_fileN}`,
          );
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setFileUploadInProgress(false);
      setFileUploadProgress(0);
      setFileUploadStatus("");
    }
  };

  const handleUnifiedFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (fileUploadInProgress) {
      AppToasty.show("Please wait for the current upload to finish", {
        type: "warning",
      });
      event.target.value = "";
      return;
    }

    const imageFiles = [];
    const videoFiles = [];
    const audioFiles = [];
    const docFiles = [];

    files.forEach((file) => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      if (
        (fileType.startsWith("image/") ||
          fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i)) &&
        allowImagesUpload
      ) {
        imageFiles.push(file);
      } else if (
        (fileType.startsWith("video/") || fileName.match(/\.(mp4)$/i)) &&
        allowVideosUpload
      ) {
        videoFiles.push(file);
      } else if (
        (fileType.startsWith("audio/") || fileName.match(/\.(wav|m4a)$/i)) &&
        allowAudiosUpload
      ) {
        audioFiles.push(file);
      } else if (allowDocsUpload) {
        const allowedExtensions = [
          ".pdf",
          ".csv",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".html",
          ".txt",
          ".md",
          ".json",
          ".pptx",
          ".ppt",
          ".py",
          ".js",
          ".java",
          ".cpp",
          ".c",
          ".cs",
          ".php",
          ".rb",
          ".swift",
          ".go",
          ".ts",
          ".rs",
          ".kt",
          ".scala",
          ".m",
          ".r",
          ".pl",
          ".sh",
          ".sql",
          ".vb",
          ".lua",
          ".dart",
          ".groovy",
          ".jl",
          ".hs",
          ".f90",
          ".pas",
          ".elm",
          ".clj",
        ];

        const hasAllowedExtension = allowedExtensions.some((ext) =>
          fileName.endsWith(ext),
        );
        if (hasAllowedExtension) {
          docFiles.push(file);
        }
      }
    });

    if (imageFiles.length > 0) {
      const needsConversion = imageFiles.some(
        (file) => file.type.startsWith("image/") && file.type !== "image/png",
      );
      if (needsConversion) {
        setFileUploadInProgress(true);
        setFileUploadStatus("converting");
        setFileUploadProgress(0);
      }

      const convertedImages = await Promise.all(
        imageFiles.map(async (file) => {
          if (file.type.startsWith("image/") && file.type !== "image/png") {
            try {
              return await convertImageToPng(file);
            } catch (error) {
              console.error("Failed to convert image:", error);
              AppToasty.show(`Failed to convert ${file.name}`, {
                type: "danger",
              });
              return null;
            }
          }
          return file;
        }),
      );
      const validImages = convertedImages.filter((f) => f !== null);
      if (validImages.length > 0) {
        setFileUploadStatus("uploading");
        handleBatchFilesSelection(validImages, "file");
      } else if (needsConversion) {
        setFileUploadInProgress(false);
        setFileUploadStatus("");
      }
    }

    if (videoFiles.length > 0) {
      if (videoFiles.length > 1) {
        AppToasty.show("Only one video can be uploaded at a time", {
          type: "warning",
        });
      }
      const videoFile = videoFiles[0];
      if (!isValidFileSize(videoFile, 50)) {
        AppToasty.show("Video file size exceeds 50MB limit", {
          type: "danger",
        });
      } else {
        handleFileSelection(videoFile, "video");
      }
    }

    if (audioFiles.length > 0) {
      handleBatchFilesSelection(audioFiles, "file");
    }

    if (docFiles.length > 0) {
      handleBatchFilesSelection(docFiles, "file");
    }

    event.target.value = "";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isStreaming) return;

    if (audioProcessingInProgress || fileUploadInProgress) {
      AppToasty.show("Please wait for the current operation to finish", {
        type: "warning",
      });
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const imageFiles = [];
    const videoFiles = [];
    const audioFiles = [];
    const docFiles = [];

    files.forEach((file) => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      if (
        (fileType.startsWith("image/") ||
          fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i)) &&
        allowImagesUpload
      ) {
        imageFiles.push(file);
      } else if (
        (fileType.startsWith("video/") ||
          fileName.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)$/i)) &&
        allowVideosUpload
      ) {
        videoFiles.push(file);
      } else if (
        (fileType.startsWith("audio/") ||
          fileName.match(/\.(mp3|wav|ogg|m4a|flac|aac|wma)$/i)) &&
        allowAudiosUpload
      ) {
        audioFiles.push(file);
      } else if (allowDocsUpload) {
        const allowedExtensions = [
          ".pdf",
          ".csv",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".html",
          ".txt",
          ".md",
          ".json",
          ".py",
          ".js",
          ".java",
          ".cpp",
          ".c",
          ".cs",
          ".php",
          ".rb",
          ".swift",
          ".go",
          ".ts",
          ".rs",
          ".kt",
          ".scala",
          ".m",
          ".r",
          ".pl",
          ".sh",
          ".sql",
          ".vb",
          ".lua",
          ".dart",
          ".groovy",
          ".jl",
          ".hs",
          ".f90",
          ".pas",
          ".elm",
          ".clj",
          ".pptx",
          ".ppt",
        ];

        const hasAllowedExtension = allowedExtensions.some((ext) =>
          fileName.endsWith(ext),
        );
        if (hasAllowedExtension) {
          docFiles.push(file);
        }
      }
    });

    if (imageFiles.length > 0) {
      handleBatchFilesSelection(imageFiles, "file");
    }

    if (videoFiles.length > 0) {
      if (videoFiles.length > 1) {
        AppToasty.show("Only one video can be uploaded at a time", {
          type: "warning",
        });
      }
      const videoFile = videoFiles[0];
      if (!isValidFileSize(videoFile, 50)) {
        AppToasty.show("Video file size exceeds 50MB limit", {
          type: "danger",
        });
      } else {
        handleFileSelection(videoFile, "video");
      }
    }

    if (audioFiles.length > 0) {
      handleBatchFilesSelection(audioFiles, "file");
    }

    if (docFiles.length > 0) {
      handleBatchFilesSelection(docFiles, "file");
    }
  };

  // Handle clipboard paste for images
  // Mac/clipboard pastes name images generically ("image"): number them so
  // consecutive pastes don't collide as the same file (image 1, image 2, ...).
  // The counter is assigned by the paste handler so the inline <image N> token
  // always matches the uploaded file name.
  const renamePastedGenericImage = (file, counter) => {
    const dotIndex = file?.name?.lastIndexOf(".") ?? -1;
    const baseName =
      dotIndex > 0 ? file.name.substring(0, dotIndex) : file.name;
    if (baseName?.toLowerCase() !== "image") return file;
    const extension = dotIndex > 0 ? file.name.substring(dotIndex) : ".png";
    return new File([file], `image ${counter}${extension}`, {
      type: file.type,
    });
  };

  // Inserts a token at the caret: a chip in the chip composer, plain text in
  // the fallback textarea. The fallback path also syncs composerText so the
  // attachment sweep sees the token (the composer updates it via onChange).
  const insertToken = (token) => {
    if (textareaRef.current?.insertToken) {
      textareaRef.current.insertToken(token);
    } else {
      insertTokenAtCursor(textareaRef.current, token, (value) => {
        setText(value);
        setComposerText(value);
      });
    }
  };

  // Clipboard capture: rich/large text becomes a file attachment referenced
  // inline with a <clipboard N> token; pure image pastes upload the image and
  // reference it with an <image N> token. Shared by the document-level
  // listener (fallback textarea mode) and the composer's onPaste.
  const handleClipboardPasteCapture = (event) => {
    if (!allowImagesUpload && !allowDocsUpload) return;

    const { htmlString, plainString, imageItem } = extractClipboardParts(
      event.clipboardData,
    );
    if (!htmlString && !plainString && !imageItem) return;

    // Pure image paste (screenshot, copied image - no text flavours):
    // upload as a file and reference it inline with an <image N> token.
    if (imageItem && !htmlString && !plainString) {
      if (!allowImagesUpload) return;
      event.preventDefault();

      const file = imageItem.getAsFile();
      if (!file) return;

      if (!isValidFileSize(file)) {
        AppToasty.show("Image size exceeds 5MB limit", {
          type: "danger",
        });
        return;
      }

      const imageCounter = ++pastedImageCounterRef.current;

      // Normalise non-PNG clipboard formats (Mac screenshots can arrive as
      // TIFF) to PNG first so downstream classification by extension works.
      (async () => {
        let fileToUpload = file;
        if (file.type?.startsWith("image/") && file.type !== "image/png") {
          try {
            fileToUpload = await convertImageToPng(file);
          } catch (error) {
            console.error("Failed to convert pasted image:", error);
          }
        }
        const renamed = renamePastedGenericImage(fileToUpload, imageCounter);
        capturedFileNamesRef.current.add(normalizeCaptureFileName(renamed.name));
        handleBatchFilesSelection([renamed], "file");
      })();
      insertToken(`<image ${imageCounter}>`);
      return;
    }

    // Rich/large text paste (Office suite, web pages, big plain text):
    // capture the clipboard content as a file attachment and reference it
    // inline with a <clipboard N> token instead of dumping a raw text blob
    // into the input.
    if (!allowDocsUpload) return;
    if (!shouldCaptureAsFile({ htmlString, plainString })) return;

    event.preventDefault();
    const clipboardCounter = ++pastedClipboardCounterRef.current;
    const file = buildClipboardFile({
      htmlString,
      plainString,
      counter: clipboardCounter,
    });

    // Same size validation as regular file uploads
    if (file.size > fileSizeConstant * 1024 * 1024) {
      AppToasty.show(
        `File ${file.name} exceeds ${fileSizeConstant}MB limit`,
        { type: "danger" },
      );
      return;
    }

    handleBatchFilesSelection([file], "file");
    capturedFileNamesRef.current.add(normalizeCaptureFileName(file.name));
    const source = detectClipboardSource(htmlString);
    setClipboardLabels((previous) => ({
      ...previous,
      [`<clipboard ${clipboardCounter}>`]: clipboardCaptureLabel(
        source,
        clipboardCounter,
      ),
    }));
    insertToken(`<clipboard ${clipboardCounter}>`);
  };

  // Keep the latest handler in a ref so the document-level listener stays
  // stable while always seeing current props/state.
  const pasteHandlerRef = useRef();
  pasteHandlerRef.current = handleClipboardPasteCapture;

  useEffect(() => {
    const listener = (event) => {
      // Pastes aimed at another editable element (search box, modal field,
      // another Arena pane) belong to that element - leave them alone. The
      // composer handles its own pastes via onPaste + stopPropagation (this
      // listener never sees them), so this document-level path serves only
      // the fallback textarea and "paste anywhere with nothing focused".
      const editableTarget = event.target?.closest?.(
        'input, textarea, [contenteditable]:not([contenteditable="false"])',
      );
      if (editableTarget && event.target !== textareaRef.current) return;
      pasteHandlerRef.current?.(event);
    };
    document.addEventListener("paste", listener);

    return () => {
      document.removeEventListener("paste", listener);
    };
  }, []);

  // Composer pastes are handled element-scoped; stop propagation so the
  // document listener does not double-handle them.
  const handleComposerPaste = (event) => {
    pasteHandlerRef.current?.(event);
    event.stopPropagation();
  };

  React.useEffect(() => {
    const handleDragEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener("dragend", handleDragEnd);

    return () => {
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, []);

  const handleKeyDown = (event) => {
    const textarea = textareaRef.current;

    if (event.key === "Enter") {
      if (textarea && event.shiftKey) {
        requestAnimationFrame(() => {
          textarea.scrollTop = textarea.scrollTop += 25;
        });
      }
      if (!event.shiftKey) {
        event.preventDefault();
        if (textareaRef.current.value?.length > 10000) {
          return AppToasty.show("Maximum 10000 characters allowed", {
            type: "danger",
          });
        }
        if (
          !isStreaming &&
          !audioProcessingInProgress &&
          !fileUploadInProgress
        ) {
          onSend(textareaRef.current.value);
          markSending();
          if (textareaRef.current) textareaRef.current.value = "";
        }
      }
    }
  };

  const handleSend = () => {
    if (textareaRef.current.value?.length > 10000) {
      return AppToasty.show("Maximum 10000 characters allowed", {
        type: "danger",
      });
    }
    if (!isStreaming && !audioProcessingInProgress && !fileUploadInProgress) {
      onSend(textareaRef.current.value);
      markSending();
      textareaRef.current.value = null;
      textareaRef.current?.focus();
    }
  };

  function getPlaceholderText() {
    if (audioProcessingInProgress) {
      return "Processing audio...";
    } else if (fileUploadInProgress) {
      if (fileUploadStatus === "converting") {
        return "Preparing files...";
      }
      return "Uploading files...";
    } else if (recordingUploadInProgress) {
      return "Transcription in progress...";
    } else if (recordingInProgress) {
      return "Listening...";
    } else {
      return text;
    }
  }

  return (
    <>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          zIndex: 30,
          position: "relative",
        }}
        className="tf-chat-input-wrapper"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {(imageUrls && imageUrls.length > 0) ||
        (audioUrls && audioUrls.length > 0) ||
        (fileUrls && fileUrls.length > 0) ||
        videoUrl ||
        recordingUploadInProgress ||
        fileUploadInProgress ? (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 4,
              paddingLeft: 20,
              paddingRight: 20,
              paddingTop: 6,
              paddingBottom: 2,
              backgroundColor: currentTheme?.theme?.lightGrey,
              borderRadius: "10px 10px 0 0",
            }}
          >
            {videoUrl && (
              <ChatMediaLeaf
                onUploaded={onVideoUploaded}
                url={videoUrl}
                type="video"
                fileName={detectFileName(videoUrl)}
                fileExtension={detectExtensionFromFileName(videoUrl)}
                agentColor={agentColor}
              />
            )}

            {fileUrls &&
              fileUrls
                .map((fileUrl, index) => ({ fileUrl, index }))
                .filter(({ fileUrl }) => isCaptureChipVisible(fileUrl))
                .map(({ fileUrl, index }) => {
                return (
                  <ChatMediaLeaf
                    key={`file-${index}`}
                    onUploaded={() =>
                      onFilesUploaded(null, null, index, "file")
                    }
                    url={fileUrl}
                    type="file"
                    fileName={detectFileName(fileUrl)}
                    fileExtension={detectExtensionFromFileName(fileUrl)}
                    agentColor={agentColor}
                  />
                );
              })}
            {imageUrls &&
              imageUrls
                .map((imageUrl, index) => ({ imageUrl, index }))
                .filter(({ imageUrl }) => isCaptureChipVisible(imageUrl))
                .map(({ imageUrl, index }) => {
                return (
                  <ChatMediaLeaf
                    key={`image-${index}`}
                    onUploaded={() =>
                      onFilesUploaded(null, null, index, "image")
                    }
                    url={imageUrl}
                    type="image"
                    fileName={detectFileName(imageUrl)}
                    fileExtension={detectExtensionFromFileName(imageUrl)}
                    agentColor={agentColor}
                  />
                );
              })}
            {audioUrls &&
              audioUrls.map((audioUrl, index) => {
                return (
                  <ChatMediaLeaf
                    key={`audio-${index}`}
                    onUploaded={() =>
                      onFilesUploaded(null, null, index, "audio")
                    }
                    url={audioUrl}
                    type="audio"
                    fileName={detectFileName(audioUrl)}
                    fileExtension={detectExtensionFromFileName(audioUrl)}
                    agentColor={agentColor}
                  />
                );
              })}
            {recordingUploadInProgress && (
              <span
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  color: currentTheme?.theme?.orange,
                }}
              >
                Transcription in progress{" "}
                <strong>{recordingUploadProgress}%</strong>
              </span>
            )}
            {fileUploadInProgress && (
              <span
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  color: currentTheme?.theme?.orange,
                }}
              >
                {fileUploadStatus === "converting"
                  ? "Preparing files..."
                  : `Uploading files ${fileUploadProgress}%`}
              </span>
            )}
          </div>
        ) : null}

        {isDragging && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: currentTheme?.theme?.primary_soft,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 999,
              pointerEvents: "none",
            }}
          >
            <span
              style={{
                fontSize: currentTheme?.theme?.medium_font_size,
                fontWeight: "bold",
                color: currentTheme?.theme?.primary,
              }}
            >
              Drop files here
            </span>
          </div>
        )}

        <div
          className="tf-chat-input"
          style={{
            width: "100%",
            flexDirection: "row",
            paddingTop: 10,
            borderRadius: isDragging ? 0 : 10,
            border: isDragging
              ? `2px dashed ${currentTheme?.theme?.primary}`
              : mode === "blue"
              ? "1px solid #456491"
              : "none",
            paddingLeft: 20,
            paddingRight: 20,
            backgroundColor: currentTheme?.theme?.lightGrey,
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          <div
            style={{ position: "relative", flex: 1, display: "flex" }}
          >
            {ENABLE_CHIP_COMPOSER ? (
              <TokenComposerInput
                ref={textareaRef}
                placeholder={getPlaceholderText()}
                currentTheme={currentTheme}
                opacity={isDragging ? 0.3 : 1}
                disabled={
                  !inputReady ||
                  audioProcessingInProgress ||
                  fileUploadInProgress
                }
                onKeyDown={handleKeyDown}
                onPaste={handleComposerPaste}
                onChange={({ text: nextText }) => setComposerText(nextText)}
                labelMap={clipboardLabels}
              />
            ) : (
              <textarea
                disabled={
                  !inputReady ||
                  audioProcessingInProgress ||
                  fileUploadInProgress
                }
                ref={textareaRef}
                placeholder={getPlaceholderText()}
                className="tf-chat-input-textarea"
                style={{
                  fontFamily: "normalFont",
                  fontSize: currentTheme?.theme?.small_font_size,
                  color: currentTheme?.theme?.font_color,
                  backgroundColor: currentTheme?.theme?.transparent,
                  flex: 1,
                  resize: "none",
                  opacity: isDragging ? 0.3 : 1,
                }}
                onKeyDown={handleKeyDown}
                onInput={(event) => setComposerText(event.target.value)}
              />
            )}
          </div>
          <div
            className="tf-chat-inout-icon"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {showUploadOption && !isStreaming && (
              <AppIcon
                isClickable
                icon={AppAssets.icons.attachment}
                size={18}
                onPress={() => fileInputRef.current.click()}
                color={agentColor || currentTheme?.theme?.darkGrey}
                wrapperStyle={{
                  marginRight: 8,
                  marginLeft: 0,
                  marginTop: 0,
                  justifyContent: "center",
                  alignSelf: "baseline",
                }}
              />
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptedFileTypes}
              multiple
              onChange={handleUnifiedFileUpload}
              style={{ display: "none" }}
            />
            {allowAudiosUpload && !isStreaming && (
              <AudioRecorder
                agentColor={agentColor}
                onRecordingCompleted={handleRecordingCompleted}
                onRecordingStarted={handleRecordingStarted}
                autoVoiceMode={autoVoiceMode}
                voiceSensitivity={voiceSensitivity}
                size={20}
                wrapperStyle={{
                  marginRight: 8,
                  marginLeft: 0,
                  marginTop: 0,
                  justifyContent: "center",
                  alignSelf: "baseline",
                }}
                workspaceId={state?.workspaceId}
              />
            )}
            <AppIcon
              icon={
                isStreaming
                  ? AppAssets?.icons?.stopGeneration
                  : AppAssets?.icons?.send
              }
              color={
                mode === "blue"
                  ? currentTheme?.theme?.white
                  : isStreaming
                  ? currentTheme?.theme?.red
                  : agentColor || currentTheme?.theme?.primary
              }
              size={20}
              wrapperStyle={{
                marginRight: 0,
                marginLeft: 0,
                marginTop: 0,
                justifyContent: "center",
                alignSelf: "baseline",
              }}
              isClickable={!isLastMessageEmpty}
              onPress={
                !isLastMessageEmpty && isStreaming ? onCancel : handleSend
              }
            />
          </div>
        </div>
      </div>

      {isChatEmpty && quickQuestions && quickQuestions?.length > 0 && (
        <div
          style={{
            width: "100%",
            display: "flex",
            alignSelf: "flex-end",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            marginTop: 10,
            marginBottom: 5,
            gap: 6,
            paddingRight: 5,
            maxWidth: 350,
          }}
        >
          {quickQuestions?.slice(0, 3)?.map((question, index) => (
            <AppText
              isClickable
              key={index}
              onPress={() => onSend(question)}
              wrapperStyle={{
                paddingVertical: 5,
                paddingRight: 10,
                paddingLeft: 5,
                borderRadius: 5,
                borderWidth: 1,
                borderColor: currentTheme?.theme?.transparent,
                backgroundColor: agentColor
                  ? hexToRGB(agentColor, 0.1)
                  : currentTheme?.theme?.primary_soft,
                maxWidth: 350,
                wordWrap: "break-word",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
              style={{
                marginLeft: 5,
                fontSize: currentTheme?.theme?.extra_small_font_size,
                textDecorationColor: agentColor || currentTheme?.theme?.primary,
                wordWrap: "break-word",
                wordBreak: "break-word",
                maxWidth: 350,
                textAlign: "end",
              }}
              color={agentColor || currentTheme?.theme?.primary}
              iconSize={16}
            >
              {question}
            </AppText>
          ))}
        </div>
      )}
    </>
  );
};

export default ChatWebInput;
